import { Renderer } from 'amis';
import { RendererProps } from 'amis';
import React, {useCallback, useEffect, useRef, useState, useMemo } from 'react';
import './styles/index.css';
import { MainContent, type MainContentRef } from './components/MainContent';
import { SidebarNav } from './components/SidebarNav';
import {getVariable, resolveVariableAndFilter, isPureVariable} from 'amis-core';

interface AIChatbotPluginProps extends RendererProps {}
type SidebarTab = 'home' | 'create' | 'search' | 'discover' | 'dify' | 'history';

interface AIChatUiProps {
  readonly defaultCapabilityCode?: string;
  readonly showAiCapabilityPage?: string;
  readonly modelLabel?: string;

  readonly onClickSidebarHome?: () => void;
  readonly onClickSidebarCreate?: () => void;
  readonly onClickSidebarSearch?: () => void;
  readonly onClickSidebarDiscover?: () => void;
  readonly onClickSidebarDify?: () => void;
  readonly onClickSidebarHistory?: () => void;

  readonly onClickUpgrade?: () => void;

  readonly onChangeInput?: (value: string) => void;
  readonly onClickMic?: () => void;
  readonly onClickSend?: () => void;

  readonly onClickAction1?: () => void;
  readonly onClickAction2?: () => void;
  readonly onClickAction3?: () => void;
  readonly onClickAction4?: () => void;
  readonly onClickDots?: () => void;
}

export const AIChatUi: React.FC<AIChatUiProps> = ({
  defaultCapabilityCode,
  showAiCapabilityPage,
  modelLabel,
  onClickSidebarHome,
  onClickSidebarCreate,
  onClickSidebarSearch,
  onClickSidebarDiscover,
  onClickSidebarDify,
  onClickSidebarHistory,
  onClickUpgrade,
  onChangeInput,
  onClickMic,
  onClickSend,
  onClickAction1,
  onClickAction2,
  onClickAction3,
  onClickAction4,
  onClickDots
}) => {
  const mainContentRef = useRef<MainContentRef>(null);
  const createConversationRef = useRef<(() => string | void) | null>(null);
  const [activeTab, setActiveTab] = useState<SidebarTab>('create');

  const handleTabChange = useCallback((newTab: SidebarTab) => {
    setActiveTab(newTab);
  }, []);

  const handleSidebarHome = useCallback(() => {
    try {
      setActiveTab('home');
      onClickSidebarHome?.();
    } catch (err) {
    }
  }, [onClickSidebarHome]);

  const handleSidebarCreate = useCallback(() => {
    try {
      setActiveTab('create');

      // 兼容外部：埋点/自定义行为
      onClickSidebarCreate?.();

      // 触发 MainContent 内部的重置逻辑（创建新会话 + 清空消息 + 回到 home 视图）
      const newId = mainContentRef.current?.createNewConversation();
      console.log('[AIChatbot][AIChatUi] handleSidebarCreate => newConvId:', newId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AIChatbot][AIChatUi] handleSidebarCreate failed', { err });
    }
  }, [onClickSidebarCreate]);

  const handleSidebarSearch = useCallback(() => {
    try {
      setActiveTab('search');
      onClickSidebarSearch?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AIChatbot][AIChatUi] handleSidebarSearch failed', { err });
    }
  }, [onClickSidebarSearch]);

  const handleSidebarDiscover = useCallback(() => {
    try {
      setActiveTab('discover');
      onClickSidebarDiscover?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AIChatbot][AIChatUi] handleSidebarDiscover failed', { err });
    }
  }, [onClickSidebarDiscover]);

  const handleSidebarDify = useCallback(() => {
    try {
      setActiveTab('dify');
      onClickSidebarDify?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AIChatbot][AIChatUi] handleSidebarDify failed', { err });
    }
  }, [onClickSidebarDify]);

  const handleSidebarHistory = useCallback(() => {
    try {
      setActiveTab('history');
      onClickSidebarHistory?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AIChatbot][AIChatUi] handleSidebarHistory failed', { err });
    }
  }, [onClickSidebarHistory]);

  const handleRegisterCreateConversation = useCallback((): string => {
    // 这个函数会被 MainContent 调用（通过 props.onCreateConversation）
    // 用来把 MainContent 内部的“创建对话”能力注册到 ref 上
    const convId = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    return convId;
  }, []);

  return (
    <div className="top-level-container">
      <div className="app-container">
        <SidebarNav
          activeTab={activeTab}
          showAiCapabilityPage={showAiCapabilityPage}
          onClickSidebarHome={handleSidebarHome}
          onClickSidebarCreate={handleSidebarCreate}
          onClickSidebarSearch={handleSidebarSearch}
          onClickSidebarDiscover={handleSidebarDiscover}
          onClickSidebarDify={handleSidebarDify}
          onClickSidebarHistory={handleSidebarHistory}
        />

        <MainContent
          ref={mainContentRef}
          modelLabel={modelLabel}
          activeTab={activeTab}
          defaultCapabilityCode={defaultCapabilityCode}
          onTabChange={handleTabChange} 
          onClickUpgrade={onClickUpgrade}
          onChangeInput={onChangeInput}
          onClickMic={onClickMic}
          onClickSend={onClickSend}
          onClickAction1={onClickAction1}
          onClickAction2={onClickAction2}
          onClickAction3={onClickAction3}
          onClickAction4={onClickAction4}
          onClickDots={onClickDots}
          onCreateConversation={() => {
            // 关键：让 MainContent 的内部逻辑可被 Sidebar 触发
            // MainContent 只要调用 onCreateConversation()，就会走它自己的 handleCreateConversation，
            // 这里返回一个新 ID 给它用（也可返回 void 让它内部生成）
            // TODO: 这里后面可以和 AiChatPage 的“新会话”打通（重置 difyConversationId 等）
            const nextId = handleRegisterCreateConversation();
            createConversationRef.current = () => nextId;
            return nextId;
          }}
        />
      </div>
    </div>
  );
};

export const AIChatbotView: React.FC<AIChatbotPluginProps> = props => {
  const {
    showAiCapabilityPage,
    data,
    aiCapabilityCode
  } = props;
  const realShowAiCapabilityPage = useMemo(() => {
      if (showAiCapabilityPage === '') {
        return 'false';
      }
      
      // 支持纯表达式：${xxx}
      if (isPureVariable(showAiCapabilityPage)) {
        const value = resolveVariableAndFilter(showAiCapabilityPage, data, '| raw');
        if (typeof value === 'string') {
          return value;
        }
        return 'false';
      }
      
      
  
      // 非纯表达式时，依然尝试做一轮 filter 解析
      const resolved = resolveVariableAndFilter(showAiCapabilityPage, data, '| raw');
      
      if (typeof resolved === 'string') {
        return resolved;
      }
  
      return showAiCapabilityPage;
    }, [showAiCapabilityPage]);
  return <AIChatUi showAiCapabilityPage={realShowAiCapabilityPage} defaultCapabilityCode={aiCapabilityCode} />;
};

@Renderer({
  test: /\bagimx-ai-chatbot$/,
  name: 'agimx-ai-chatbot'
})
export default class AIChatbotPlugin extends React.Component<AIChatbotPluginProps> {
  // render() {
  //   return <AIChatbotView {...this.props} />;
  // }
  render(): React.ReactNode {
    return (
      <div style={{ height: this.props.height }}>
        <AIChatbotView {...this.props} />
      </div>
    );
  }
}